import { Box, Stack, Typography, Divider } from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import Note from "../../library/Note";
import { formatMoney, getPricing } from "./subscriptionPricing";

export default function DiscountPreview({ subscription, percent }) {
  const current = getPricing(subscription);
  const proposed = getPricing({
    ...subscription,
    custom_discount_percent: percent,
  });

  const isUnchanged =
    subscription.custom_discount_percent != null &&
    Number(subscription.custom_discount_percent) === percent;

  if (percent == null || isUnchanged) {
    return (
      <Typography variant='caption' color='text.secondary'>
        {percent == null
          ? "Enter a discount between 0% and 100% (max. 1 decimal)."
          : `Discount matches the saved setting. ${current.discount.source}.`}
      </Typography>
    );
  }

  const hasPrice =
    current.expectedRecurring != null && proposed.expectedRecurring != null;

  return (
    <Stack spacing={2}>
      {/* Comparison */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
        <Box sx={{ flex: 1 }}>
          <Typography variant='caption' color='text.secondary'>
            Discount
          </Typography>

          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mt: 0.5 }}
          >
            <Typography variant='body2' color='text.secondary'>
              {current.discount.percent ?? "Unknown"}%
            </Typography>

            <ArrowForwardRounded
              sx={{ fontSize: 16, color: "text.disabled" }}
            />

            <Typography variant='subtitle1' fontWeight={700}>
              {percent}%
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            {current.discount.source}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant='caption' color='text.secondary'>
            Per billing period
          </Typography>

          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            flexWrap='wrap'
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                textDecoration: hasPrice ? "line-through" : "none",
              }}
            >
              {formatMoney(current.expectedRecurring, current.currency)}
            </Typography>

            <ArrowForwardRounded
              sx={{ fontSize: 16, color: "text.disabled" }}
            />

            <Typography
              variant='subtitle1'
              fontWeight={700}
              color='primary.main'
            >
              {formatMoney(proposed.expectedRecurring, current.currency)}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Divider />

      {/* Footnote */}
      <Note
        sx={{ p: 0, bgcolor: "transparent" }}
        iconSx={{ fontSize: 15, color: "text.disabled", mt: 0.2 }}
      >
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ lineHeight: 1.6 }}
        >
          {!hasPrice && "Some pricing details are unavailable. "}
          This is a preview of the expected recurring charges after applying the
          discount. This replaces the saved subscription discount, including any
          promo discount. Discounts do not stack.
          {percent === 0 &&
            " A 0% manual discount overrides the Beta discount."}
        </Typography>
      </Note>
    </Stack>
  );
}
